import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Building2,
  Crown,
  RefreshCw,
  Loader2,
  Mail,
  Phone,
  Copy,
  ExternalLink,
  MessageSquare,
  Send,
  ChevronRight,
  CheckCircle2,
  X,
  StickyNote,
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

const authHeaders = () => {
  const t = localStorage.getItem('le_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const fmtDate = (v) => {
  if (!v) return '—';
  try {
    const d = new Date(v);
    const now = new Date();
    const diffMs = now - d;
    const diffH = diffMs / (1000 * 60 * 60);
    if (diffH < 1) return `${Math.max(1, Math.floor(diffMs / 60000))}m ago`;
    if (diffH < 24) return `${Math.floor(diffH)}h ago`;
    if (diffH < 24 * 7) return `${Math.floor(diffH / 24)}d ago`;
    return d.toLocaleDateString();
  } catch {
    return v;
  }
};

// Status vocabulary — matches the backend PATCH endpoint's slug rules.
const STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' },
];

const STATUS_TONE = {
  new: 'bg-primary/10 text-primary border-primary/20',
  contacted: 'bg-sky-500/10 text-sky-600 border-sky-500/20',
  qualified: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
  won: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
  lost: 'bg-muted text-muted-foreground border-border',
};

const PLAN_TABS = [
  {
    value: 'quarterly_leadership',
    label: 'Quarterly',
    icon: Crown,
    hint: '\u20b948k / $1,490 · 90 days',
    testid: 'admin-enquiries-tab-quarterly',
  },
  {
    value: 'enterprise',
    label: 'Enterprise',
    icon: Building2,
    hint: 'Custom retainer',
    testid: 'admin-enquiries-tab-enterprise',
  },
];

/**
 * Reply templates — dropped as `mailto:` bodies pre-filled with the
 * enquiry context so admins can respond in one click. Kept short and
 * benefit-led on purpose; a strategist can edit before sending.
 */
const REPLY_TEMPLATES = {
  quarterly_leadership: [
    {
      key: 'strategy_call',
      label: 'Book a strategy call',
      subject: 'Blayzrr Executive Leadership Quarterly — quick call?',
      body: (row) =>
        `Hi ${row.name || 'there'},\n\nThanks for reaching out about the Executive Leadership Quarterly engagement. I read what you shared${row.message ? ` about ${extractFirstLine(row.message)}` : ''} and I\u2019d love a 20-minute call to understand what a great outcome would look like for you over the next 90 days.\n\nHere are a couple of times \u2014 pick whichever works and I\u2019ll send a calendar invite:\n  \u2022 [Weekday] 11:00 IST / 12:30 GMT\n  \u2022 [Weekday] 17:00 IST / 06:30 PST\n\nOr feel free to grab any slot directly: https://cal.com/blayzrr/quarterly-intro\n\nWarmly,\nThe Blayzrr team`,
    },
    {
      key: 'starter_brief',
      label: 'Send the starter brief',
      subject: 'Blayzrr Executive Leadership Quarterly — brief attached',
      body: (row) =>
        `Hi ${row.name || 'there'},\n\nThanks for the note about ${row.company || 'your business'}. Attaching a short brief covering exactly what a 90-day Blayzrr Quarterly engagement covers, sample deliverables, and typical outcomes for founders / senior leaders in your position.\n\nWhen you\u2019re done reading, would a 20-minute intro call this week work? Happy to hold a slot \u2014 just tell me the day.\n\nWarmly,\nThe Blayzrr team`,
    },
    {
      key: 'nurture',
      label: 'Not yet — keep in touch',
      subject: 'Blayzrr — no pressure, staying in touch',
      body: (row) =>
        `Hi ${row.name || 'there'},\n\nCompletely hear you \u2014 90 days of managed executive presence is a real commitment and only makes sense at the right moment. I\u2019ll stop pushing and keep you on our founder-notes list.\n\nIf you\u2019d like a lighter first step, our Full Profile Makeover (\u20b9999 / $49) rewrites your whole profile in one sitting: https://blayzrr.com/pricing\n\nEither way \u2014 whenever the timing is right, my inbox is open.\n\nWarmly,\nThe Blayzrr team`,
    },
  ],
  enterprise: [
    {
      key: 'intro_call',
      label: 'Book a scoping call',
      subject: 'Blayzrr Enterprise \u2014 scoping call?',
      body: (row) =>
        `Hi ${row.name || 'there'},\n\nThanks for reaching out about Blayzrr Enterprise for ${row.company || 'your team'}. To scope this well, would a 30-minute call this week work? I\u2019d like to understand team size, roles you\u2019d want us to focus on, and what a successful pilot looks like for you.\n\nGrab any slot: https://cal.com/blayzrr/enterprise-scoping\n\nWarmly,\nThe Blayzrr team`,
    },
    {
      key: 'sample_deck',
      label: 'Send sample deliverables',
      subject: 'Blayzrr Enterprise \u2014 sample deliverables & pricing',
      body: (row) =>
        `Hi ${row.name || 'there'},\n\nGood to meet you. Attaching a short deck with sample deliverables from prior enterprise engagements plus pricing bands based on team size. Have a skim and let me know which parts feel most relevant to ${row.company || 'your goals'} \u2014 then we can jump on a scoping call.\n\nWarmly,\nThe Blayzrr team`,
    },
  ],
};

const extractFirstLine = (msg) => {
  const s = String(msg || '').trim().replace(/\s+/g, ' ');
  return s.length > 90 ? `${s.slice(0, 90)}…` : s;
};

const StatusBadge = ({ status }) => {
  const s = (status || 'new').toLowerCase();
  const cls = STATUS_TONE[s] || STATUS_TONE.new;
  return (
    <Badge
      variant="outline"
      className={`uppercase text-[10px] tracking-wider font-mono-edge ${cls}`}
      data-testid="admin-enquiry-status-badge"
    >
      {s}
    </Badge>
  );
};

/**
 * EnterpriseEnquiriesInbox — Feb-2026 tabbed CRM surface for the two
 * lead capture surfaces: Quarterly (\u20b948k / $1,490 · 90 days) and
 * Enterprise (custom retainer). Both share the same underlying
 * ``enterprise_enquiries`` collection, differentiated by ``plan``.
 *
 * Features:
 *   \u2022 Tab per surface with row counts.
 *   \u2022 Row click opens a detail drawer with the enquiry's message,
 *     status control, note field, and one-click reply templates.
 *   \u2022 Every reply template pre-fills a ``mailto:`` body with the
 *     enquiry context so admins can respond without leaving the CRM.
 *   \u2022 Status + note persist via PATCH /api/admin/enterprise/enquiries/{id}.
 */
export function EnterpriseEnquiriesInbox() {
  const [activeTab, setActiveTab] = useState('quarterly_leadership');
  const [itemsByPlan, setItemsByPlan] = useState({ quarterly_leadership: [], enterprise: [] });
  const [loadingByPlan, setLoadingByPlan] = useState({ quarterly_leadership: false, enterprise: false });
  const [selectedId, setSelectedId] = useState(null);

  const loadOne = useCallback(async (plan) => {
    setLoadingByPlan((s) => ({ ...s, [plan]: true }));
    try {
      const res = await axios.get(`${BACKEND}/api/admin/enterprise/enquiries`, {
        headers: authHeaders(),
        params: { plan, limit: 200 },
        timeout: 20000,
      });
      setItemsByPlan((s) => ({ ...s, [plan]: res?.data?.items || [] }));
    } catch (err) {
      toast.error(`Could not load ${plan === 'quarterly_leadership' ? 'quarterly' : 'enterprise'} enquiries.`);
    } finally {
      setLoadingByPlan((s) => ({ ...s, [plan]: false }));
    }
  }, []);

  // Prime both tabs on mount so the counts show up immediately.
  useEffect(() => {
    loadOne('quarterly_leadership');
    loadOne('enterprise');
  }, [loadOne]);

  const rows = itemsByPlan[activeTab] || [];
  const selectedRow = useMemo(
    () =>
      Object.values(itemsByPlan)
        .flat()
        .find((r) => r?.id === selectedId) || null,
    [itemsByPlan, selectedId],
  );

  const patchRow = async (row, updates) => {
    try {
      const res = await axios.patch(
        `${BACKEND}/api/admin/enterprise/enquiries/${row.id}`,
        updates,
        { headers: authHeaders(), timeout: 15000 },
      );
      const next = res?.data?.item;
      if (!next) throw new Error('empty response');
      const plan = next.plan || 'enterprise';
      setItemsByPlan((s) => ({
        ...s,
        [plan]: (s[plan] || []).map((r) => (r.id === next.id ? next : r)),
      }));
      toast.success('Saved.');
    } catch (err) {
      toast.error('Could not save the change.');
    }
  };

  return (
    <>
      <Card className="border-border mt-6" data-testid="admin-enterprise-enquiries-card">
        <CardContent className="p-0">
          <div className="p-4 border-b border-border flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-2 min-w-0">
              <Building2 size={18} className="text-primary shrink-0" aria-hidden="true" />
              <h2 className="font-semibold whitespace-nowrap">Sales inbox</h2>
              <span className="text-xs text-muted-foreground hidden md:inline truncate">
                Quarterly & Enterprise leads from the Pricing / Landing pages.
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                loadOne('quarterly_leadership');
                loadOne('enterprise');
              }}
              disabled={loadingByPlan.quarterly_leadership || loadingByPlan.enterprise}
              className="ml-auto"
              data-testid="admin-enterprise-enquiries-refresh"
            >
              {loadingByPlan.quarterly_leadership || loadingByPlan.enterprise ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <RefreshCw size={14} />
              )}
            </Button>
          </div>

          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="w-full"
            data-testid="admin-enquiries-tabs"
          >
            <div className="px-4 pt-3">
              <TabsList className="h-10 rounded-xl">
                {PLAN_TABS.map((t) => {
                  const Icon = t.icon;
                  const count = (itemsByPlan[t.value] || []).length;
                  return (
                    <TabsTrigger
                      key={t.value}
                      value={t.value}
                      className="h-8 gap-2 px-3"
                      data-testid={t.testid}
                    >
                      <Icon size={13} strokeWidth={2.2} aria-hidden="true" />
                      <span>{t.label}</span>
                      <Badge
                        variant="secondary"
                        className="rounded-full text-[10px] px-1.5 h-5 font-mono-edge"
                        data-testid={`${t.testid}-count`}
                      >
                        {count}
                      </Badge>
                    </TabsTrigger>
                  );
                })}
              </TabsList>
            </div>
            {PLAN_TABS.map((t) => (
              <TabsContent
                key={t.value}
                value={t.value}
                className="mt-0"
                data-testid={`${t.testid}-panel`}
              >
                <PlanTableInner
                  plan={t.value}
                  hint={t.hint}
                  rows={itemsByPlan[t.value] || []}
                  loading={!!loadingByPlan[t.value]}
                  onSelect={(row) => setSelectedId(row.id)}
                />
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>

      <EnquiryDetailSheet
        row={selectedRow}
        open={!!selectedRow}
        onOpenChange={(open) => {
          if (!open) setSelectedId(null);
        }}
        onPatch={patchRow}
      />
    </>
  );
}

const PlanTableInner = ({ plan, hint, rows, loading, onSelect }) => {
  if (loading && rows.length === 0) {
    return (
      <div className="p-8 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
        <Loader2 size={14} className="animate-spin" /> Loading…
      </div>
    );
  }
  if (!loading && rows.length === 0) {
    return (
      <div
        className="p-8 text-center text-sm text-muted-foreground"
        data-testid={`admin-enquiries-${plan}-empty`}
      >
        <p className="font-medium text-foreground">Nothing here yet.</p>
        <p className="mt-1">{hint}</p>
      </div>
    );
  }
  return (
    <div className="max-h-[420px] overflow-auto">
      <Table>
        <TableHeader className="sticky top-0 bg-card z-10">
          <TableRow>
            <TableHead className="w-[110px]">Received</TableHead>
            <TableHead>Company</TableHead>
            <TableHead>Contact</TableHead>
            <TableHead className="hidden md:table-cell">Message</TableHead>
            <TableHead className="w-[120px]">Status</TableHead>
            <TableHead className="w-[40px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={row.id}
              className="cursor-pointer hover:bg-muted/40"
              onClick={() => onSelect(row)}
              data-testid={`admin-enquiries-${plan}-row-${row.id}`}
            >
              <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                {fmtDate(row.created_at)}
              </TableCell>
              <TableCell className="font-medium">{row.company}</TableCell>
              <TableCell>
                <div className="min-w-0">
                  <div className="truncate">{row.name}</div>
                  <div className="text-[11px] text-muted-foreground truncate">
                    {row.work_email}
                  </div>
                </div>
              </TableCell>
              <TableCell className="hidden md:table-cell text-xs text-muted-foreground max-w-[280px]">
                <span className="line-clamp-1">
                  {row.message || <span className="italic">no message</span>}
                </span>
              </TableCell>
              <TableCell>
                <StatusBadge status={row.status} />
              </TableCell>
              <TableCell className="text-muted-foreground">
                <ChevronRight size={14} aria-hidden="true" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

/**
 * Slide-in detail drawer for a single enquiry. Shows the full message,
 * a status editor, a note field, and the one-click reply templates for
 * the enquiry's plan.
 */
const EnquiryDetailSheet = ({ row, open, onOpenChange, onPatch }) => {
  const [note, setNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);

  // Reset local note buffer whenever the visible row changes.
  useEffect(() => {
    setNote(row?.note || '');
  }, [row?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!row) return null;
  const plan = row.plan || 'enterprise';
  const templates = REPLY_TEMPLATES[plan] || REPLY_TEMPLATES.enterprise;

  const handleStatus = async (v) => {
    setStatusSaving(true);
    try {
      await onPatch(row, { status: v });
    } finally {
      setStatusSaving(false);
    }
  };

  const handleSaveNote = async () => {
    setSavingNote(true);
    try {
      await onPatch(row, { note });
    } finally {
      setSavingNote(false);
    }
  };

  const openMailto = (tpl) => {
    const subject = encodeURIComponent(tpl.subject);
    const body = encodeURIComponent(tpl.body(row));
    const url = `mailto:${row.work_email}?subject=${subject}&body=${body}`;
    window.open(url, '_blank', 'noopener');
    // Bump status to 'contacted' if we're still 'new' \u2014 a soft
    // heuristic so admins don't have to remember two clicks.
    if ((row.status || 'new').toLowerCase() === 'new') {
      onPatch(row, { status: 'contacted' });
    }
  };

  const copyBody = async (tpl) => {
    try {
      await navigator.clipboard.writeText(tpl.body(row));
      toast.success('Reply copied to clipboard.');
    } catch {
      toast.error('Could not copy — check clipboard permissions.');
    }
  };

  const isQuarterly = plan === 'quarterly_leadership';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-lg overflow-y-auto"
        data-testid="admin-enquiry-detail-sheet"
      >
        <SheetHeader className="space-y-3">
          <div className="flex items-center gap-2">
            <Badge
              variant="outline"
              className={`uppercase text-[10px] tracking-wider font-mono-edge ${
                isQuarterly
                  ? 'bg-primary/10 text-primary border-primary/20'
                  : 'bg-muted text-foreground border-border'
              }`}
            >
              {isQuarterly ? (
                <Crown size={11} className="mr-1" aria-hidden="true" />
              ) : (
                <Building2 size={11} className="mr-1" aria-hidden="true" />
              )}
              {isQuarterly ? 'Quarterly' : 'Enterprise'}
            </Badge>
            <span className="text-xs text-muted-foreground">
              Received {fmtDate(row.created_at)}
            </span>
          </div>
          <SheetTitle className="text-xl leading-tight" data-testid="admin-enquiry-detail-title">
            {row.company}
          </SheetTitle>
        </SheetHeader>

        <div className="mt-5 space-y-5">
          {/* Contact */}
          <div className="rounded-xl border border-border bg-muted/30 p-3">
            <div className="text-[10px] font-mono-edge tracking-[0.22em] uppercase text-muted-foreground">
              Contact
            </div>
            <div className="mt-2 space-y-1.5 text-sm">
              <div className="font-medium">{row.name}</div>
              <a
                href={`mailto:${row.work_email}`}
                className="text-primary hover:underline inline-flex items-center gap-1.5 text-[13px]"
                data-testid="admin-enquiry-detail-email"
              >
                <Mail size={12} aria-hidden="true" />
                {row.work_email}
              </a>
              <div>
                <a
                  href={`tel:${row.phone}`}
                  className="text-primary hover:underline inline-flex items-center gap-1.5 text-[13px]"
                  data-testid="admin-enquiry-detail-phone"
                >
                  <Phone size={12} aria-hidden="true" />
                  {row.phone}
                </a>
              </div>
            </div>
          </div>

          {/* Message */}
          <div>
            <div className="text-[10px] font-mono-edge tracking-[0.22em] uppercase text-muted-foreground mb-2 flex items-center gap-1.5">
              <MessageSquare size={11} aria-hidden="true" />
              Their message
            </div>
            <div
              className="rounded-xl border border-border p-3 text-sm whitespace-pre-wrap leading-relaxed bg-background"
              data-testid="admin-enquiry-detail-message"
            >
              {row.message || (
                <span className="italic text-muted-foreground">
                  No message shared — they only filled the basics.
                </span>
              )}
            </div>
          </div>

          {/* Status */}
          <div>
            <div className="text-[10px] font-mono-edge tracking-[0.22em] uppercase text-muted-foreground mb-2">
              Pipeline status
            </div>
            <Select
              value={(row.status || 'new').toLowerCase()}
              onValueChange={handleStatus}
              disabled={statusSaving}
            >
              <SelectTrigger data-testid="admin-enquiry-status-select" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s.value} value={s.value} data-testid={`status-opt-${s.value}`}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {row.updated_by ? (
              <p className="mt-1.5 text-[11px] text-muted-foreground">
                Last touched by {row.updated_by} · {fmtDate(row.updated_at)}
              </p>
            ) : null}
          </div>

          {/* Reply templates */}
          <div>
            <div className="text-[10px] font-mono-edge tracking-[0.22em] uppercase text-muted-foreground mb-2 flex items-center gap-1.5">
              <Send size={11} aria-hidden="true" />
              One-click replies
            </div>
            <div className="space-y-2" data-testid="admin-enquiry-detail-templates">
              {templates.map((tpl) => (
                <div
                  key={tpl.key}
                  className="rounded-xl border border-border p-3 bg-background flex items-center gap-2"
                  data-testid={`admin-enquiry-reply-tpl-${tpl.key}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm">{tpl.label}</div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      {tpl.subject}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8"
                    onClick={() => copyBody(tpl)}
                    data-testid={`admin-enquiry-reply-copy-${tpl.key}`}
                  >
                    <Copy size={13} className="mr-1" />
                    Copy
                  </Button>
                  <Button
                    size="sm"
                    className="h-8"
                    onClick={() => openMailto(tpl)}
                    data-testid={`admin-enquiry-reply-send-${tpl.key}`}
                  >
                    <ExternalLink size={13} className="mr-1" />
                    Send
                  </Button>
                </div>
              ))}
            </div>
            <p className="mt-2 text-[11px] text-muted-foreground">
              Sending marks the enquiry as <b>Contacted</b> automatically.
            </p>
          </div>

          {/* Internal note */}
          <div>
            <div className="text-[10px] font-mono-edge tracking-[0.22em] uppercase text-muted-foreground mb-2 flex items-center gap-1.5">
              <StickyNote size={11} aria-hidden="true" />
              Internal note
            </div>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add a note only the team can see (call outcome, decision-maker, next step)…"
              className="min-h-[96px]"
              data-testid="admin-enquiry-note-input"
            />
            <div className="mt-2 flex justify-end">
              <Button
                size="sm"
                onClick={handleSaveNote}
                disabled={savingNote || note === (row.note || '')}
                data-testid="admin-enquiry-note-save"
              >
                {savingNote ? (
                  <Loader2 size={13} className="mr-1 animate-spin" />
                ) : (
                  <CheckCircle2 size={13} className="mr-1" />
                )}
                Save note
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default EnterpriseEnquiriesInbox;
